/*
"What's available offline" state for a round's *round data* (the confirmed
aggregate: proposals, evals, notes, allocations).

Scope: this owns the round-DATA cache only. Offline **video** availability is a
separate concern owned by the iPad per-round video download (#2434 — Gui's
`iphone/lib/mobileVideoStore` + `useDownloadQueue`); read video-download state
from there, not here. (An earlier draft reserved a `videosCached` field as an
integration seam, but that work shipped with its own store and never consumed
it, so the dead field was removed.) Round data and video bytes are cached by
entirely separate machinery — videos are explicitly out of the round-data cache.
*/

import { SPAStorageAdapter } from "@engine/storageAdapters/SPAStorageAdapter";
import { useNetworkStatus } from "@client/hooks/useNetworkStatus";
import { useEffect, useState } from "react";

export type OfflineAvailability = {
  /** The round's confirmed aggregate (proposals, evals, notes, allocations…). */
  aggregateCached: boolean;
  /** When the aggregate cache was last refreshed (epoch ms), or null. */
  cachedAt: number | null;
};

export async function getOfflineAvailability(
  roundId: string,
): Promise<OfflineAvailability> {
  const meta = await SPAStorageAdapter.readCachedAggregateMeta(roundId);
  return {
    aggregateCached: meta != null,
    cachedAt: meta?.cachedAt ?? null,
  };
}

const EMPTY: OfflineAvailability = { aggregateCached: false, cachedAt: null };

/** React hook wrapper around {@link getOfflineAvailability}. */
export function useOfflineAvailability(roundId: string): OfflineAvailability {
  const [availability, setAvailability] = useState<OfflineAvailability>(EMPTY);
  // Re-read when connectivity flips so the banner reflects the latest cache
  // metadata (e.g. the "cached … ago" suffix) when the funder goes offline
  // mid-session, rather than the once-per-mount snapshot.
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    let cancelled = false;
    void getOfflineAvailability(roundId).then((result) => {
      if (!cancelled) {
        setAvailability(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [roundId, isOnline]);

  return availability;
}
